import React, {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { TooltipBubble, TooltipTrigger } from "./Tooltip.styles";
import type { TooltipPlacement, TooltipProps } from "./Tooltip.types";

interface TooltipPosition {
  top: number;
  left: number;
  placement: TooltipPlacement;
}

const VIEWPORT_GAP = 8;
const TOOLTIP_GAP = 8;

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  disabled = false,
  onlyWhenOverflowed = false,
  placement = "top",
  maxWidth = "360px",
  block = false,
}) => {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const hasOverflow = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return false;

    const firstChild = trigger.firstElementChild as HTMLElement | null;
    const target = firstChild ?? trigger;

    return (
      target.scrollWidth > target.clientWidth + 1 ||
      target.scrollHeight > target.clientHeight + 1
    );
  }, []);

  const canShow = useCallback(() => {
    if (disabled || content === null || content === undefined || content === "") {
      return false;
    }

    return !onlyWhenOverflowed || hasOverflow();
  }, [content, disabled, hasOverflow, onlyWhenOverflowed]);

  const show = useCallback(() => {
    if (!canShow()) return;
    setPosition(null);
    setVisible(true);
  }, [canShow]);

  const hide = useCallback(() => {
    setVisible(false);
    setPosition(null);
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    let resolvedPlacement = placement;
    let top =
      placement === "top"
        ? triggerRect.top - tooltipRect.height - TOOLTIP_GAP
        : triggerRect.bottom + TOOLTIP_GAP;

    if (placement === "top" && top < VIEWPORT_GAP) {
      resolvedPlacement = "bottom";
      top = triggerRect.bottom + TOOLTIP_GAP;
    } else if (
      placement === "bottom" &&
      top + tooltipRect.height > window.innerHeight - VIEWPORT_GAP
    ) {
      resolvedPlacement = "top";
      top = triggerRect.top - tooltipRect.height - TOOLTIP_GAP;
    }

    top = Math.max(
      VIEWPORT_GAP,
      Math.min(top, window.innerHeight - tooltipRect.height - VIEWPORT_GAP),
    );

    const centeredLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    const left = Math.max(
      VIEWPORT_GAP,
      Math.min(centeredLeft, window.innerWidth - tooltipRect.width - VIEWPORT_GAP),
    );

    setPosition({ top, left, placement: resolvedPlacement });
  }, [placement]);

  useLayoutEffect(() => {
    if (!visible) return undefined;

    updatePosition();

    const handleViewportChange = () => updatePosition();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [updatePosition, visible]);

  return (
    <>
      <TooltipTrigger
        ref={triggerRef}
        $block={block}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocusCapture={show}
        onBlurCapture={hide}
        onKeyDown={(event) => {
          if (event.key === "Escape") hide();
        }}
        aria-describedby={visible ? tooltipId : undefined}
      >
        {children}
      </TooltipTrigger>

      {visible && typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <TooltipBubble
            id={tooltipId}
            ref={tooltipRef}
            role="tooltip"
            $placement={position?.placement ?? placement}
            $maxWidth={maxWidth}
            $positioned={Boolean(position)}
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
            }}
          >
            {content}
          </TooltipBubble>,
          document.body,
        )}
    </>
  );
};

export default Tooltip;
